import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import ballotService from '../../services/ballotService';
import categoryService from '../../services/categoryService';
import { useBeta } from '../../contexts/BetaContext';
import { useSettings } from '../../contexts/SettingsContext';
import { getCategoryWinnerIds, getCategoryWinnersText } from '../../utils/categoryWinners';
import './PublicLeaderboardPage.css';

const PartyResultsPage = () => {
  const { activePartyId } = useBeta();
  const { settings, loading: settingsLoading } = useSettings();
  const [ballots, setBallots] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!activePartyId) {
      setLoading(false);
      return;
    }

    let isMounted = true;

    const loadResults = async () => {
      setLoading(true);
      setError('');

      try {
        const [fetchedBallots, fetchedCategories] = await Promise.all([
          ballotService.fetchBallots(activePartyId),
          categoryService.fetchCategories(activePartyId),
        ]);

        if (!isMounted) return;

        setBallots(fetchedBallots);
        setCategories(fetchedCategories);
      } catch (loadError) {
        if (!isMounted) return;
        setError(loadError.message || 'Failed to load results.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadResults();

    return () => {
      isMounted = false;
    };
  }, [activePartyId]);

  const partyStartDateValue = settings?.partyStartDate || settings?.partyDate;
  const partyDate = partyStartDateValue ? new Date(partyStartDateValue) : null;
  const resultsYear = settings?.year || (partyDate && !Number.isNaN(partyDate.getTime())
    ? partyDate.getFullYear()
    : new Date().getFullYear());
  const hasPartyEnded = Boolean(settings?.dateEnded);

  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) => {
      const orderA = a.order ?? 999;
      const orderB = b.order ?? 999;
      return orderA - orderB;
    });
  }, [categories]);

  const resultsData = useMemo(() => {
    return ballots.map((ballot) => {
      const selectionsMap = new Map();
      if (ballot.userCategoriesSelections) {
        ballot.userCategoriesSelections.forEach((sel) => {
          selectionsMap.set(sel.categoryId, sel.selection?.nomineeId);
        });
      }

      const categoryScores = {};
      sortedCategories.forEach((category) => {
        const categoryId = category.categoryId || category.id;
        const winnerIds = getCategoryWinnerIds(category);
        const userSelection = selectionsMap.get(categoryId);
        const categoryPoints = category.points || 1;

        if (winnerIds.size === 0) {
          categoryScores[categoryId] = null;
        } else if (winnerIds.has(userSelection)) {
          categoryScores[categoryId] = categoryPoints;
        } else {
          categoryScores[categoryId] = 0;
        }
      });

      return {
        id: ballot.id,
        name: ballot.userDisplayName || ballot.userName || 'Unknown',
        totalScore: ballot.totalScore || 0,
        categoryScores,
      };
    });
  }, [ballots, sortedCategories]);

  const sortedResults = useMemo(() => {
    const sorted = [...resultsData].sort((a, b) => {
      if (b.totalScore !== a.totalScore) {
        return b.totalScore - a.totalScore;
      }
      return (a.name || '').localeCompare((b.name || ''), undefined, { sensitivity: 'base' });
    });

    let currentRank = 1;
    return sorted.map((player, index) => {
      if (index > 0 && player.totalScore < sorted[index - 1].totalScore) {
        currentRank = index + 1;
      }
      return {
        ...player,
        rank: currentRank,
      };
    });
  }, [resultsData]);

  const getCategoryHeaderLabel = (label) => {
    const value = (label || '').trim();
    const parts = value.split(/\s+/);
    if (parts.length === 2) {
      return `${parts[0]}\n${parts[1]}`;
    }
    return value;
  };

  const getCategoryLabelText = (category) => (
    category.abbreviatedName || category.name || ''
  ).trim();

  const isScreenplayCategory = (category) => {
    const text = getCategoryLabelText(category).toLowerCase();
    return text.includes('screenplay');
  };

  const isInternationalCategory = (category) => {
    const text = getCategoryLabelText(category).toLowerCase();
    return text.includes('international');
  };

  const getCategoryColumnClassName = (category) => {
    const classNames = [];
    if (isScreenplayCategory(category)) classNames.push('category-col--screenplay');
    if (isInternationalCategory(category)) classNames.push('category-col--international');
    return classNames.join(' ');
  };

  if (settingsLoading) {
    return (
      <div className="public-leaderboard-page">
        <div className="leaderboard-container">
          <div className="past-winners-loading">
            <div className="loading-spinner"></div>
            <p>Loading results...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!hasPartyEnded) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="public-leaderboard-page">
        <div className="leaderboard-container">
          <div className="past-winners-loading">
            <div className="loading-spinner"></div>
            <p>Loading results...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-leaderboard-page">
        <div className="leaderboard-container">
          <div className="empty-state">
            <p>Error loading results: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="public-leaderboard-page">
      <div className="leaderboard-container">
        <header className="leaderboard-header">
          <h1 className="leaderboard-title">
            <span className="leaderboard-title__glow">{resultsYear} Results</span>
          </h1>
          <p className="leaderboard-subtitle"><em>You Must Be Present to Win!!</em></p>
        </header>

        <section className="leaderboard-shell">
          <div className="leaderboard-shell__inner">
            {sortedResults.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📊</div>
                <h2>No scores yet</h2>
                <p>The results will appear here once ballots are submitted and scored.</p>
              </div>
            ) : (
              <div className="leaderboard-table-wrapper">
                <table className="leaderboard-table leaderboard-table-detailed">
                  <thead>
                    <tr className="header-row-titles">
                      <th className="th-rank">Rank</th>
                      <th className="th-name">Name</th>
                      <th className="th-score">Score</th>
                      {sortedCategories.map((category) => (
                        <th
                          key={category.categoryId || category.id}
                          className={`th-category ${getCategoryColumnClassName(category)}`.trim()}
                        >
                          <span
                            className={`category-header-text ${isInternationalCategory(category) ? 'category-header-text--single-line' : ''}`.trim()}
                            title={category.abbreviatedName}
                          >
                            {getCategoryHeaderLabel(category.abbreviatedName)}
                          </span>
                        </th>
                      ))}
                    </tr>
                    <tr className="header-row-winners">
                      <th className="th-winner-label" colSpan={3}>Winners</th>
                      {sortedCategories.map((category) => {
                        const winnerText = getCategoryWinnersText(category);
                        return (
                          <th
                            key={category.categoryId || category.id}
                            className={`th-winner ${getCategoryColumnClassName(category)}`.trim()}
                          >
                            <span className="winner-text" title={winnerText}>
                              {winnerText || '—'}
                            </span>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedResults.map((player, index) => (
                      <tr key={player.id || index}>
                        <td className="rank-cell">
                          <span className="rank-number">{player.rank}.</span>
                        </td>
                        <td className="name-cell">
                          <span className="player-name">{player.name}</span>
                        </td>
                        <td className="score-cell">
                          <span className="score-value">{player.totalScore}</span>
                        </td>
                        {sortedCategories.map((category) => {
                          const categoryId = category.categoryId || category.id;
                          const score = player.categoryScores[categoryId];

                          return (
                            <td
                              key={categoryId}
                              className={`category-score-cell ${score === null ? 'pending' : score > 0 ? 'correct' : 'incorrect'} ${getCategoryColumnClassName(category)}`.trim()}
                            >
                              {score === null ? '' : score}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default PartyResultsPage;
